import { useEffect, useId, useRef, type ReactNode } from "react";
import { Icon, SortableList } from "@omnabase/ui";
import { FORM_LIMITS, FORM_MAPPING_LABELS, FORM_TYPE_LABELS, isFormBlock } from "@omnabase/shared";
import type { DraftField } from "../../../lib/formEdits";
import { TYPE_ICONS } from "./AddFieldMenu";

/** The builder's fields (builder v2, the founder's design; round one's table "Fields" retired): the list "Fields", one row per field. A row
 *  is two sibling buttons, the kit's handle ("Drag to reorder <label>", editors only) and the row's toggle (named by the label, the muted line
 *  "Short text · Required" as its description), and opens in place into the field's settings, one row at a time. The drag is the kit's
 *  `SortableList` (pointer or Space, arrows, Space); above the rows "Fields · drag to reorder" and "6 of 40" (Text blocks count as items). A
 *  Text block's row is named by its words, its line "Text · H1". */
export function FieldList({ items, open, edit, problems, reveal, onToggle, onMove, settingsOf }: {
  items: readonly DraftField[]; open: string | null; edit: boolean; problems: ReadonlySet<string>;
  /** a row to scroll into view (a click in the preview, a field just added), with a counter so the same row can be asked twice */ reveal: { uid: string; n: number } | null;
  onToggle: (uid: string) => void; onMove: (from: number, to: number) => void; settingsOf: (f: DraftField) => ReactNode;
}) {
  const rows = useRef(new Map<string, HTMLLIElement>());
  useEffect(() => { if (reveal) rows.current.get(reveal.uid)?.scrollIntoView({ block: "nearest" }); }, [reveal]);
  return (
    <div className="ob-form-builder__fields">
      <div className="ob-form-builder__eyebrow"><span className="ob-form-builder__eyebrow-text">{edit ? "Fields · drag to reorder" : "Fields"}</span><span className="ob-form-builder__count">{items.length} of {FORM_LIMITS.fields}</span></div>
      <SortableList ids={items.map((f) => f.uid)} onDrop={onMove}>
        {(drag) => (
          <ul className="ob-form-builder__list" aria-label="Fields">
            {items.map((f, index) => (
              <FieldRow key={f.uid} field={f} open={f.uid === open} problem={problems.has(f.uid)} handle={edit ? drag.handle(index, rowName(f)) : null}
                rowProps={drag.rowProps(index, `ob-form-builder__row ob-form-builder__field${f.uid === open ? " ob-form-builder__row--open" : ""}`)}
                onToggle={() => onToggle(f.uid)} rowRef={(el) => { if (el) rows.current.set(f.uid, el); else rows.current.delete(f.uid); }}>
                {f.uid === open ? settingsOf(f) : null}
              </FieldRow>
            ))}
          </ul>
        )}
      </SortableList>
    </div>
  );
}

/** the row's name: a field's label, a Text block's words on one line (an empty block "Empty text") */
const rowName = (f: DraftField): string => (isFormBlock(f) ? f.text.replace(/\s+/g, " ").trim() || "Empty text" : f.label.trim() || "Untitled field");
/** the muted line under a row's name: a field's type, the detail it stands for, Required; a Text block's "Text" and its element ("H1",
 *  "P"); a problem when there is one */
export const fieldLine = (f: DraftField, problem: boolean): string => {
  const parts = isFormBlock(f) ? [FORM_TYPE_LABELS.block, f.as.toUpperCase()] : [FORM_TYPE_LABELS[f.type], f.mapping ? FORM_MAPPING_LABELS[f.mapping] : "", f.required ? "Required" : ""];
  return [...parts, problem ? "Needs attention" : ""].filter(Boolean).join(" · ");
};

function FieldRow({ field: f, open, problem, handle, rowProps, onToggle, rowRef, children }: {
  field: DraftField; open: boolean; problem: boolean; handle: ReactNode; rowProps: { "data-drag-row": ""; className: string | undefined };
  onToggle: () => void; rowRef: (el: HTMLLIElement | null) => void; children: ReactNode;
}) {
  const id = useId();
  const name = rowName(f);
  return (
    <li {...rowProps} ref={rowRef}>
      <div className="ob-form-builder__row-head">
        {handle}
        <button type="button" className="ob-form-builder__toggle-row" aria-expanded={open} aria-controls={open ? `${id}-settings` : undefined} aria-labelledby={`${id}-label`} aria-describedby={`${id}-line`} onClick={onToggle}>
          <span className="ob-form-builder__mark"><Icon name={TYPE_ICONS[f.type]} size="sm" /></span>
          <span className="ob-form-builder__row-text"><span className="ob-form-builder__row-title" id={`${id}-label`}>{name}</span><span className={`ob-form-builder__row-line${problem ? " ob-form-builder__row-line--problem" : ""}`} id={`${id}-line`}>{fieldLine(f, problem)}</span></span>
          <Icon name={open ? "chevronUp" : "chevronDown"} size="sm" className="ob-form-builder__chevron" />
        </button>
      </div>{" " /* the muted line and the open settings read as two phrases, never one word */}
      {open && <div className="ob-form-builder__row-body" role="region" id={`${id}-settings`} aria-label={`${name} settings`}>{children}</div>}
    </li>
  );
}
